import { useCallback } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useSelfScheduling } from "../../../contexts/SelfScheduling/SelfSchedulingContext";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import type { NurseInShift } from "../../../utils/models";
import { offersReplacementSearch } from "./offersReplacementSearch";
import { DeleteRecurringOrSingleMode } from "./types";

/**
 * Per-shift "would removing this open a call-off with the replacement search?" for a surface that lists
 * shifts, so it can mark the remove controls the call-off walkthrough may press.
 *
 * Reads the same contexts the removal dialog does and runs the same predicate for a single-shift removal,
 * so a marked control can never open a dialog without the checkbox.
 */
export const useRemovalOffersReplacementSearch = (showActual: boolean): ((shift: NurseInShift) => boolean) => {
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const { isDraftMode } = useDraftMode();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const { chosenDepartment } = useChosenDepartment();
	return useCallback(
		(shift: NurseInShift): boolean =>
			offersReplacementSearch({
				start: shift.startTime,
				department: chosenDepartment,
				isCallOffEnabled,
				isDraftMode,
				inSelfSchedulingPeriod: isDataInSelfSchedulingPeriod(shift.startTime),
				showActual,
				mode: DeleteRecurringOrSingleMode.DeleteSingle,
			}),
		[chosenDepartment, isCallOffEnabled, isDraftMode, isDataInSelfSchedulingPeriod, showActual],
	);
};
